/**
 * Input — eDMS Design System
 *
 * Source: drafts/31_ux_design_system_spec.md §3.2
 *
 * Anatomy:
 *   [Label (above; never as placeholder per UX spec)]
 *   [Input]
 *   [Helper text / Error message]
 *
 * Accessibility:
 * - <label for=""> linking to input id.
 * - aria-invalid="true" when error.
 * - aria-describedby pointing to helper/error.
 * - Error announced via role="alert" on the error <p>.
 * - 16px minimum font-size (prevents mobile zoom on iOS).
 */

import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';
import { clsx } from 'clsx';
import styles from './Input.module.css';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label: string;
  helperText?: string;
  errorText?: string;
  /** Visually hide label (still readable by screen readers). */
  hideLabel?: boolean;
  isRequired?: boolean;
  size?: 'sm' | 'md' | 'lg';
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
  isFullWidth?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      helperText,
      errorText,
      hideLabel = false,
      isRequired = false,
      size = 'md',
      leadingIcon,
      trailingIcon,
      isFullWidth = false,
      id,
      className,
      disabled,
      readOnly,
      ...rest
    },
    ref
  ) => {
    const generatedId = useId();
    const inputId = id ?? `input-${generatedId}`;
    const helperId = helperText ? `${inputId}-helper` : undefined;
    const errorId = errorText ? `${inputId}-error` : undefined;
    const describedBy = errorId ?? helperId;

    return (
      <div className={clsx(styles.field, isFullWidth && styles.fullWidth, className)}>
        <label
          htmlFor={inputId}
          className={clsx(styles.label, hideLabel && 'visually-hidden')}
        >
          {label}
          {isRequired && (
            <span aria-hidden="true" className={styles.required}>
              *
            </span>
          )}
        </label>

        <div
          className={clsx(
            styles.inputWrapper,
            styles[size],
            errorText && styles.hasError,
            disabled && styles.disabled,
            readOnly && styles.readonly
          )}
        >
          {leadingIcon && (
            <span className={styles.icon} aria-hidden="true">
              {leadingIcon}
            </span>
          )}
          <input
            ref={ref}
            id={inputId}
            disabled={disabled}
            readOnly={readOnly}
            required={isRequired}
            aria-invalid={errorText ? 'true' : undefined}
            aria-describedby={describedBy}
            className={styles.input}
            {...rest}
          />
          {trailingIcon && (
            <span className={styles.icon} aria-hidden="true">
              {trailingIcon}
            </span>
          )}
        </div>

        {errorText && (
          <p id={errorId} role="alert" className={styles.errorText}>
            {errorText}
          </p>
        )}
        {!errorText && helperText && (
          <p id={helperId} className={styles.helperText}>
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';
